import React from 'react'
import _ from 'lodash'
import PropTypes from 'prop-types'
import TitleContentColumns from './TitleContentColumns'
import ModuleSandboxActivityCards from './ModuleSandboxActivityCards'
import AssignmentPopupContainer from '../containers/AssignmentPopupContainer/index'
import IFrameWrapper from './IFrameWrapper'
import {
	formatKeysToCamelCase,
	formatStudentKeysToCamelCase,
	getSandboxName,
	getSandboxQuestDescription
} from '../util'
import '../assets/css/components/module-sandbox.css'
import { runQuery } from '../fetchUtils'

const ModuleSandbox = ({ module, studentGroupingsProps }) => {
	const [isAssignmentPopupVisible, setIsAssignmentPopupVisible] = React.useState(false)
	const [sandboxAssignableContent, setSandboxAssignableContent] = React.useState({})

	React.useEffect(() => {
		handleFetchSandboxUnlockQuest(module.id)
	}, [])

	const handleFetchSandboxUnlockQuest = eduModuleUnlockId => {
		const sandboxUnlockQuestQuery = `
      query LoadSandboxUnlockQuest($eduModuleUnlockId: ID!){
        sandboxQuest(eduModuleUnlockId:$eduModuleUnlockId){
          id
          name
          sandboxQuestDescription: inGameDescription
          eduModuleUnlockId
          sandboxUnlockModule{
            sandboxName
          }
        }
      }
    `
		const vars = {
			eduModuleUnlockId: eduModuleUnlockId
		}

		const fetchSanboxUnlockQuest = {
			onComplete: json => {
				const error = json.error
				if (!_.isEmpty(error)) {
					console.error(error)
				} else if (_.isNull(json.data.sandboxQuest)) {
					console.log('No sandbox related quest found')
				} else {
					setSandboxAssignableContent(prevState => ({
						...prevState,
						...formatSandboxAssignableQuest(json.data.sandboxQuest)
					}))
				}
			},
			onFailure: error => {
				console.error(error)
			}
		}
		runQuery(sandboxUnlockQuestQuery, vars, fetchSanboxUnlockQuest)
	}

	const formatSandboxAssignableQuest = sandboxQuest => {
		return {
			...formatKeysToCamelCase(module),
			id: sandboxQuest.id,
			educationalDescription: getSandboxQuestDescription(
				sandboxQuest.sandboxQuestDescription,
				sandboxQuest.sandboxUnlockModule.sandboxName
			),
			sandboxName: sandboxQuest.sandboxUnlockModule.sandboxName,
			name: getSandboxName(sandboxQuest.sandboxUnlockModule.sandboxName, sandboxQuest.name)
		}
	}

	const getIframeUrl = () => {
		switch (module.id) {
			case '1':
				return 'https://www.youtube.com/embed/ICVpf343rrg'
			case '2':
				return 'https://www.youtube.com/embed/o4vRL2uhHIg'
			case '6':
				return 'https://www.youtube.com/embed/k_yHaSYxgRk'
			default:
				return ''
		}
	}

	const getSandboxProgressionItems = () => {
		return _.map(module.sandbox_progression_items, item => <li key={item}>{item}</li>)
	}
	const getSandboxFeatures = () => {
		return _.map(module.sandbox_feature_items, item => <li key={item}>{item}</li>)
	}

	const getSandboxLimitations = () => {
		return _.map(module.sandbox_limitation_items, item => <li key={item}>{item}</li>)
	}

	return (
		<div className='module-subpage module-sandbox'>
			<h1>
				<span className='semi-bold'>Sandbox</span>
			</h1>

			<div className='card card-transparent'>
				<div className='card-block d-flex flex-wrap'>
					<div className='description-row col-lg-10 no-padding'>
						{module.sandbox_description}
					</div>
					<br />
					<div className='iframe-row col-lg-8 no-padding'>
						{getIframeUrl() && (
							<IFrameWrapper>
								<iframe
									title='embedded sandbox overview video'
									src={getIframeUrl()}
									frameBorder='0'
									allow='autoplay; encrypted-media'
									allowFullScreen></iframe>
							</IFrameWrapper>
						)}
					</div>
				</div>
			</div>

			<br />
			<div className='progression-list'>
				<TitleContentColumns title='Progression'>
					{_.isEmpty(module.sandbox_progression_items) ? (
						<p>Sorry, we're still working on this feature!</p>
					) : (
						<ul>{getSandboxProgressionItems()}</ul>
					)}
				</TitleContentColumns>
			</div>

			<br />
			<div className='features-and-limitations-wrapper'>
				<table className='features-and-limitations'>
					<thead>
						<tr>
							<th>Features</th>
							<th>Limitations</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td>
								{_.isEmpty(module.sandbox_feature_items) ? (
									<p>Sorry, we're still working on this feature!</p>
								) : (
									<ul>{getSandboxFeatures()}</ul>
								)}
							</td>
							<td>
								{_.isEmpty(module.sandbox_limitation_items) ? (
									<p>Sorry, we're still working on this feature!</p>
								) : (
									<ul>{getSandboxLimitations()}</ul>
								)}
							</td>
						</tr>
					</tbody>
				</table>
			</div>

			<button
				onClick={() => setIsAssignmentPopupVisible(true)}
				className='assign-sandbox btn btn-success btn-lg'>
				Assign Intro Quest
			</button>

			{isAssignmentPopupVisible && !_.isEmpty(sandboxAssignableContent) && (
				<AssignmentPopupContainer
					assignableContent={sandboxAssignableContent}
					studentGroupings={formatStudentKeysToCamelCase(
						studentGroupingsProps.collection
					)}
					handleClose={() => setIsAssignmentPopupVisible(false)}
					moduleId={module.id}
					usingDeletionForm={false}
				/>
			)}

			<br />
			<div className='sandbox-activities-wrapper'>
				<h2>
					<span className='semi-bold'>Sandbox Activities</span>
				</h2>
				<br />
				<p>
					Sandboxes are designed to be flexible so that you can use classroom supports for
					a variety of approaches. The below are suggested activities. They will not
					assign anything in the game, so they will need to be assigned with the materials
					provided.
				</p>
				<ModuleSandboxActivityCards module={module} />
			</div>
		</div>
	)
}

ModuleSandbox.propTypes = {
	module: PropTypes.object.isRequired
}

export default ModuleSandbox
